import { isValidCnpj } from '../party/cnpj.js';
import { isValidCpf } from '../party/cpf.js';
import { partyDescriptor } from '../descriptors/party.descriptor.js';
import { mergeErrors } from './mergeErrors.js';
import { when } from './rules.js';
import { runDescriptorValidation } from './runDescriptorValidation.js';
import { validateAddress } from './validateAddress.js';
import { validateWithRules } from './validateWithRules.js';
/**
 * L0 + L1 validation for Party. Single authority for usePartyFormConfig.onValidate,
 * partyService.createParty/updateParty, and future mobile/direct writers.
 *
 * Errors are keyed by the **domain** fields — the roles error comes back under
 * `roles`; the console remaps it to its checkbox field (`roleCustomer`).
 * CPF uses `isValidCpf` (11 digits + DV). CNPJ uses `isValidCnpj` so the
 * alphanumeric form (IN RFB 2.229/2024) is accepted, not only 14 digits.
 * Doc **uniqueness** (L2) stays in the service (`_assertDocUnique` — needs IO).
 *
 * The address block validates in **text mode**, and that is deliberate — do not
 * "fix" it to `{ mode: 'reference' }` (spec 0047 D24). The mode belongs to the
 * **write path**, not to the entity, and `Party` has three write paths: the
 * console form, which does pick city and state from the lists, plus two
 * importers that only ever have text — `InvoiceImportPromoteDialog` (promoting
 * the NF-e issuer, whose address comes from a third party's XML and can never
 * carry an id of ours) and `legacyRowMappers.toParty`. Since
 * `partyService.createParty` validates before writing, demanding the ids here
 * would refuse every import with an address — **in runtime, with the typecheck
 * green**. The console form adds `{ mode: 'reference' }` in its own
 * `onValidate`, where a list to choose from actually exists.
 *
 * What text mode still enforces is D16: an empty block is fine — a party with no
 * address is ordinary — but a started one owes the minimum of a postable
 * address.
 */
export function validateParty(entity, ctx) {
    return mergeErrors(runDescriptorValidation(partyDescriptor, entity, [
        'code',
        'partyType',
        'displayName',
        'tradeName',
        'legalName',
    ]), (entity.roles ?? []).length === 0
        ? { roles: 'Selecione ao menos um papel: Cliente ou Fornecedor.' }
        : undefined, validateWithRules(entity, {
        cpf: [
            when((e) => e.partyType === 'PF', (v) => {
                if (v == null || v === '')
                    return undefined;
                return isValidCpf(String(v)) ? undefined : 'CPF inválido.';
            }),
        ],
        cnpj: [
            when((e) => e.partyType === 'PJ', (v) => {
                if (v == null || v === '')
                    return undefined;
                return isValidCnpj(String(v)) ? undefined : 'CNPJ inválido.';
            }),
        ],
    }, ctx), 
    // Text mode (the default) on purpose — see the note above (D24).
    validateAddress(entity));
}
//# sourceMappingURL=validateParty.js.map